import { trackConfig, type IFramework } from '@/framework';
import {
    LayerType,
    VectorType,
    type ILayerProps,
    type IVectorEffect,
    type IVectorEffectReflection,
    type IVectorEffectShadow,
    type IVectorLayerProps,
} from '@/version-types';
import { pushHistory, requestVectorSVGPreview, updateLayer } from '../../utils';

type EffectKind = keyof IVectorEffect;

type Options =
    | { kind: 'shadow'; config: IVectorEffectShadow }
    | { kind: 'reflection'; config: IVectorEffectReflection };

export interface IBaseEffect {
    /**
     * 添加特效
     * @param layerID - 图层 id
     * @param options - 特效配置
     * @returns
     */
    add(layerID: string, options: Options): ILayerProps | void;

    /**
     * 移除特效
     * @param layerID - 图层 id
     * @param kind - 特效类型
     * @returns
     */
    remove(layerID: string, kind: EffectKind): ILayerProps | void;

    /**
     * 连续调整阴影或倒影属性。
     *
     * @param layerID - 目标图层 ID。
     * @param kind - 需要调整的特效类型。
     * @param props - 合并到当前特效的数据。
     * @param options - 是否提交历史；false 时使用 SVG preview。
     * @returns 更新后的图层；目标或特效无效时返回 undefined。
     */
    adjustEffectProps(
        layerID: string,
        kind: 'shadow',
        props: Partial<IVectorEffectShadow>,
        options?: { writeHistory: boolean },
    ): ILayerProps | void;
    adjustEffectProps(
        layerID: string,
        kind: 'reflection',
        props: Partial<IVectorEffectReflection>,
        options?: { writeHistory: boolean },
    ): ILayerProps | void;
    adjustEffectProps(
        layerID: string,
        kind: EffectKind,
        props: Partial<IVectorEffectShadow | IVectorEffectReflection>,
        options?: { writeHistory: boolean },
    ): ILayerProps | void;

    /**
     * 设置特效可见性
     * @param layerID - 图层 id
     * @param kind - 特效类型
     * @param flag - 可见性
     * @returns
     */
    setVisibility(layerID: string, kind: EffectKind, flag: boolean): ILayerProps | void;
}

export class BaseEffect implements IBaseEffect {
    private prevLayer: ILayerProps | null = null;

    constructor(private framework: IFramework) {}

    add(layerID: string, options: Options): ILayerProps | void {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const prevLayerData = prevLayer.data as IVectorLayerProps;
        const { vectorType } = prevLayerData;

        if (vectorType === VectorType.Graph) return;

        const { kind, config } = options;
        const { effect: prevEffect } = prevLayerData;

        const nextEffect: IVectorEffect = { ...prevEffect, [kind]: [config] };
        const nextLayerData: IVectorLayerProps = { ...prevLayerData, effect: nextEffect };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.VECTOR_LAYER][kind]);

        return nextLayer;
    }

    remove(layerID: string, kind: EffectKind) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const prevLayerData = prevLayer.data as IVectorLayerProps;
        const { vectorType } = prevLayerData;

        if (vectorType === VectorType.Graph) return;

        const { effect: prevEffect } = prevLayerData;
        const prevTargeEffect = (prevEffect[kind] || [])[0];

        if (!prevTargeEffect) return;

        const nextEffect: IVectorEffect = { ...prevEffect, [kind]: [] };
        const nextLayerData: IVectorLayerProps = { ...prevLayerData, effect: nextEffect };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.VECTOR_LAYER][kind]);

        return nextLayer;
    }

    adjustEffectProps(
        layerID: string,
        kind: EffectKind,
        props: Partial<IVectorEffectShadow | IVectorEffectReflection>,
        options = { writeHistory: true },
    ) {
        const prevLayer = (() => {
            if (this.prevLayer?.id === layerID) return this.prevLayer;
            this.prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
            return this.prevLayer;
        })();
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const prevLayerData = prevLayer.data as IVectorLayerProps;
        const { vectorType } = prevLayerData;

        if (vectorType === VectorType.Graph) return;

        const { writeHistory } = options;
        if (writeHistory) {
            const nextLayer = this.framework.service.flatLayer.getLayer(layerID, true);

            requestVectorSVGPreview(this.framework, 'commit', nextLayer);
            updateLayer(this.framework, nextLayer);

            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.VECTOR_LAYER][kind]);
            this.prevLayer = null;

            return nextLayer;
        }

        const { effect: prevEffect } = prevLayerData;
        const prevTargeEffect = (prevEffect[kind] || [])[0];

        if (!prevTargeEffect) return;

        const nextTargetEffect: IVectorEffectReflection | IVectorEffectShadow = { ...prevTargeEffect, ...props };
        const nextEffect: IVectorEffect = { ...prevEffect, [kind]: [nextTargetEffect] };
        const nextLayerData: IVectorLayerProps = { ...prevLayerData, effect: nextEffect };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        requestVectorSVGPreview(this.framework, 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { dataOnly: true });

        return nextLayer;
    }

    setVisibility(layerID: string, kind: EffectKind, flag: boolean) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const prevLayerData = prevLayer.data as IVectorLayerProps;
        const { vectorType } = prevLayerData;

        if (vectorType === VectorType.Graph) return;

        const { effect: prevEffect } = prevLayerData;
        const prevTargeEffect = (prevEffect[kind] || [])[0];

        if (!prevTargeEffect) return;

        const nextTargetEffect: IVectorEffectReflection | IVectorEffectShadow = { ...prevTargeEffect, hidden: flag };
        const nextEffect: IVectorEffect = { ...prevEffect, [kind]: [nextTargetEffect] };

        const nextLayerData: IVectorLayerProps = { ...prevLayerData, effect: nextEffect };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.VECTOR_LAYER][kind]);

        return nextLayer;
    }
}
